import { StateCreator } from 'zustand';
import { getPanelKeyFromRef } from '@/core';
import { Middleware } from './common';
import { HistorySlice } from './history-slice';
import { PanelGroupItemId, PanelGroupSlice } from './panel-group-slice';
import { PanelSlice } from './panel-slice';

/**
 * Slice that handles the visual editor state and actions for deleting Panels.
 */
export interface DeletePanelSlice {
  /**
   * Delete panels
   */
  deletePanel: (panelGroupItemId: PanelGroupItemId) => void;

  /**
   * State for the delete panel dialog when it's open, otherwise undefined when it's closed.
   */
  deletePanelDialog?: DeletePanelDialogState;

  /**
   * Open delete panel dialog
   */
  openDeletePanelDialog: (panelGroupItemId: PanelGroupItemId) => void;

  /**
   * Close delete panel dialog
   */
  closeDeletePanelDialog: () => void;
}

export interface DeletePanelDialogState {
  panelGroupItemId: PanelGroupItemId;
  panelName: string;
  panelGroupName: string;
}

/**
 * Curried function for creating the PanelDeleteSlice.
 */
export function createDeletePanelSlice(): StateCreator<
  // Actions in here need to modify both Panels and Panel Groups state
  DeletePanelSlice & PanelSlice & PanelGroupSlice & HistorySlice,
  Middleware,
  [],
  DeletePanelSlice
> {
  // Return the state creator function for Zustand that uses the panels provided as intitial state
  return (set, get) => ({
    deletePanel(panelGroupItemId: PanelGroupItemId) {
      let existingPanelKey: string | undefined;

      // For undo shortcut, ensure we have a baseline snapshot so the very first action can be undone
      get().initializeHistoryIfEmpty();

      set((draft) => {
        const { panelGroupId, panelGroupItemLayoutId: panelGroupLayoutId } = panelGroupItemId;

        const existingGroup = draft.panelGroups[panelGroupId];
        if (existingGroup === undefined) {
          throw new Error(`Missing panel group ${panelGroupId}`);
        } else if (existingGroup.kind !== 'Grid' || !panelGroupLayoutId) {
          throw new Error(`Panel group ${panelGroupId} is not a Grid panel group`);
        }
        const existingLayoutIdx = existingGroup.itemLayouts.findIndex((layout) => layout.i === panelGroupLayoutId);
        existingPanelKey = existingGroup.itemPanelKeys[panelGroupLayoutId];
        if (existingLayoutIdx === -1 || existingPanelKey === undefined) {
          throw new Error(`Missing panel group item ${panelGroupLayoutId}`);
        }

        // remove panel from panel group
        existingGroup.itemLayouts.splice(existingLayoutIdx, 1);
        delete existingGroup.itemPanelKeys[panelGroupLayoutId];

        // See if panel key is still used and if not, delete it
        if (isPanelKeyStillUsed(draft.panelGroups, existingPanelKey) === false) {
          delete draft.panels[existingPanelKey];
        }
      });

      // Push snapshot AFTER changes so currentIndex points at the mutated state
      if (existingPanelKey) {
        get().pushToHistory({
          actionId: `delete-panel-${existingPanelKey}`,
          actionName: `Deleted panel: ${existingPanelKey}`,
        });
      }
    },

    openDeletePanelDialog(panelGroupItemId: PanelGroupItemId) {
      const { panelGroupId, panelGroupItemLayoutId: panelGroupLayoutId } = panelGroupItemId;

      const { panels, panelGroups } = get();
      const panelGroup = panelGroups[panelGroupId];
      if (panelGroup === undefined) {
        throw new Error(`Panel group not found ${panelGroupId}`);
      } else if (panelGroup.kind !== 'Grid' || !panelGroupLayoutId) {
        throw new Error(`Panel group ${panelGroupId} is not a Grid panel group`);
      }

      const panelKey = panelGroup.itemPanelKeys[panelGroupLayoutId];
      if (panelKey === undefined) {
        throw new Error(`Could not find Panel Group item ${panelGroupLayoutId}`);
      }

      const panel = panels[panelKey];
      if (panel === undefined) {
        throw new Error(`Could not find panel ${panelKey}`);
      }

      set((state) => {
        state.deletePanelDialog = {
          panelGroupItemId: panelGroupItemId,
          panelName: panel.spec.display.name ?? '',
          panelGroupName: panelGroup.title ?? '',
        };
      });
    },

    closeDeletePanelDialog() {
      set((state) => {
        state.deletePanelDialog = undefined;
      });
    },
  });
}

// Helper function to determine if a panel key is still being used somewhere in Panel Groups
function isPanelKeyStillUsed(panelGroups: PanelGroupSlice['panelGroups'], panelKey: string) {
  for (const group of Object.values(panelGroups)) {
    if (group.kind === 'Grid') {
      const found = Object.values(group.itemPanelKeys).find((key) => key === panelKey);
      if (found !== undefined) {
        return true;
      }
    }

    if (group.kind === 'SplitPanel') {
      return panelKey === getPanelKeyFromRef(group.content);
    }
  }
  return false;
}
